import { Component, Suspense, useEffect, useState, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth, passkeySupported } from "./auth/AuthProvider";
import { SignIn, RegisterDevicePrompt } from "./auth/SignIn";
import { LinkProblem, SetUpSignIn } from "./auth/SetUpSignIn";
import { BrandLoading } from "./brand/Brand";
import { AppShell } from "./shell/AppShell";
import { ToastProvider } from "./ui/toast";
import { Button, Loading } from "./ui/primitives";
import { recordClientError } from "./lib/errors";
import { routes } from "./routes";
import { isOnline } from "./offline/useOnline";
import { startAutoSync } from "./offline/sync";
import { SyncNotices } from "./offline/SyncNotices";
import { NotKeptNotice } from "./offline/NotKeptNotice";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: (n) => n < 1 && isOnline(), refetchOnWindowFocus: true, networkMode: "always" } }
});

function NotLinked() {
  const { signOut } = useAuth();
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 text-center">
      <h1 className="text-[26px]">Your login is not linked yet</h1>
      <p className="mt-2 text-muted">Ask the CEO to link your login to your name in Settings, People.</p>
      <button type="button" className="mt-6 min-h-[44px] text-primary" onClick={() => void signOut()}>Sign out</button>
    </main>
  );
}

/**
 * ED-RK-020: a screen's code that cannot be loaded (no signal, a lost connection) says so in plain words, from code that is already loaded,
 * instead of leaving the person on the screen before (in the installed app, with no address bar, that is "nothing happens"). Nothing reloads
 * by itself; the person chooses. It is reset by going to another address.
 */
class ScreenFailed extends Component<{ children: ReactNode; screen: string }, { failed: Error | null }> {
  override state: { failed: Error | null } = { failed: null };
  static getDerivedStateFromError(error: unknown) { return { failed: error instanceof Error ? error : new Error(String(error)) }; }
  override componentDidCatch(error: unknown, info: { componentStack?: string | null }) {
    recordClientError(error instanceof Error ? error.message : String(error), { screen: this.props.screen, componentStack: info.componentStack ?? undefined });
  }
  override render() {
    const { failed } = this.state;
    if (!failed) return this.props.children;
    const codeNotLoaded = /dynamically imported module|Importing a module script failed|Loading chunk/i.test(failed.message);
    return (
      <div role="alert" className="mx-auto max-w-md py-10 text-center">
        <h1 className="text-[28px] leading-tight md:text-[32px] text-ink">This screen could not be loaded</h1>
        <p className="mt-2 text-muted">
          {codeNotLoaded
            ? "There may be no signal, or the app has just been updated. Tap Try again when there is signal."
            : "Something went wrong on this screen. The problem has been logged. Tap Try again, or choose another screen below."}
        </p>
        <Button variant="secondary" className="mt-4" onClick={() => window.location.reload()}>Try again</Button>
      </div>
    );
  }
}

function RouteScreen({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ScreenFailed key={pathname} screen={pathname}><Suspense fallback={<Loading />}>{children}</Suspense></ScreenFailed>;
}

function Gate() {
  const { ready, session, me, offline, linkProblem, setUpPending } = useAuth();
  useEffect(() => (session || offline ? startAutoSync() : undefined), [session, offline]);
  const [skippedRegister, setSkippedRegister] = useState(() => sessionStorage.getItem("rk-skip-register") === "1");
  if (!ready) return <BrandLoading label="Starting" />;
  if (linkProblem && !session) return <LinkProblem />;
  if (!session && !(offline && me)) return <SignIn />;
  if (!me) return <NotLinked />;
  if (setUpPending && !offline) return <SetUpSignIn />;
  // Also skipped when "Set up your sign-in" was told this device has no Face ID or fingerprint.
  const skipped = skippedRegister || sessionStorage.getItem("rk-skip-register") === "1";
  if (!offline && !me.has_passkey && passkeySupported() && !skipped) return <RegisterDevicePrompt onDone={() => { sessionStorage.setItem("rk-skip-register", "1"); setSkippedRegister(true); }} />;
  return (
    <Routes>
      <Route element={<><SyncNotices /><NotKeptNotice /><AppShell /></>}>
        <Route index element={<Navigate to="/today" replace />} />
        {routes.map((r) => (
          <Route key={r.path} path={r.path} element={<RouteScreen><r.Component /></RouteScreen>} />
        ))}
        <Route path="*" element={<Navigate to="/today" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          {/* ED-RK-020: no React transition around a tap's navigation, so while the next screen's code is on its way the Loading screen shows at once. */}
          <BrowserRouter useTransitions={false}>
            <Gate />
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
